import { PRODUCT_FEEDBACK_LIMITS } from '../types/productFeedback';

export const PRODUCT_FEEDBACK_SOURCE_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const PRODUCT_FEEDBACK_SCREENSHOT_MAX_DIMENSION = 2560;
export const PRODUCT_FEEDBACK_SCREENSHOT_QUALITIES = [0.9, 0.78, 0.66, 0.54] as const;
export const PRODUCT_FEEDBACK_SCREENSHOT_SCALES = [1, 0.9, 0.8, 0.7] as const;

const SUPPORTED_SOURCE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export interface ProductFeedbackDecodedImage {
  width: number;
  height: number;
  source: CanvasImageSource;
  dispose: () => void;
}

export interface ProductFeedbackImageRuntime {
  decode: (source: Blob) => Promise<ProductFeedbackDecodedImage>;
  encodeJpeg: (
    decoded: ProductFeedbackDecodedImage,
    width: number,
    height: number,
    quality: number
  ) => Promise<Blob>;
}

export interface ProductFeedbackProcessedScreenshot {
  blob: Blob;
  width: number;
  height: number;
  quality: number;
  attempts: number;
}

const defaultRuntime: ProductFeedbackImageRuntime = {
  decode: async (source) => {
    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(source);
      return {
        width: bitmap.width,
        height: bitmap.height,
        source: bitmap,
        dispose: () => bitmap.close(),
      };
    }

    const objectUrl = URL.createObjectURL(source);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = () => reject(new Error('Nie udało się zdekodować obrazu.'));
        element.src = objectUrl;
      });
      return {
        width: image.naturalWidth,
        height: image.naturalHeight,
        source: image,
        dispose: () => URL.revokeObjectURL(objectUrl),
      };
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      throw error;
    }
  },
  encodeJpeg: async (decoded, width, height, quality) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Przeglądarka nie udostępnia bezpiecznego kodowania obrazu.');
    context.drawImage(decoded.source, 0, 0, width, height);
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Nie udało się zakodować obrazu JPEG.'));
        },
        'image/jpeg',
        quality
      );
    });
  },
};

export const preprocessProductFeedbackScreenshot = async (
  source: Blob,
  runtime: ProductFeedbackImageRuntime = defaultRuntime
): Promise<ProductFeedbackProcessedScreenshot> => {
  if (!SUPPORTED_SOURCE_TYPES.has(source.type)) {
    throw new Error('Obsługiwane są wyłącznie obrazy JPEG, PNG i WebP.');
  }
  if (source.size > PRODUCT_FEEDBACK_SOURCE_IMAGE_MAX_BYTES) {
    throw new Error('Plik źródłowy przekracza limit 10 MiB.');
  }

  const decoded = await runtime.decode(source);
  try {
    if (
      !Number.isFinite(decoded.width) ||
      !Number.isFinite(decoded.height) ||
      decoded.width < 1 ||
      decoded.height < 1
    ) {
      throw new Error('Obraz ma niepoprawne wymiary.');
    }
    const initialScale = Math.min(
      1,
      PRODUCT_FEEDBACK_SCREENSHOT_MAX_DIMENSION / Math.max(decoded.width, decoded.height)
    );
    const baseWidth = Math.max(1, Math.round(decoded.width * initialScale));
    const baseHeight = Math.max(1, Math.round(decoded.height * initialScale));
    let attempts = 0;

    for (const scale of PRODUCT_FEEDBACK_SCREENSHOT_SCALES) {
      const width = Math.max(1, Math.round(baseWidth * scale));
      const height = Math.max(1, Math.round(baseHeight * scale));
      for (const quality of PRODUCT_FEEDBACK_SCREENSHOT_QUALITIES) {
        attempts += 1;
        const blob = await runtime.encodeJpeg(decoded, width, height, quality);
        if (blob.type !== 'image/jpeg') {
          throw new Error('Kodek nie zwrócił canonical pliku JPEG.');
        }
        if (blob.size <= PRODUCT_FEEDBACK_LIMITS.maxAttachmentBytes) {
          return { blob, width, height, quality, attempts };
        }
      }
    }
    throw new Error('Nie udało się przygotować obrazu mieszczącego się w limicie 1 MiB.');
  } finally {
    decoded.dispose();
  }
};
